import { useEffect, useRef, useState } from 'react';
import { CHECKOUT_TAB_STATES } from '../constants/bookingConst';
import { formatCountdown, msUntil } from '../utils/countdownUtil';
import { isBookingSubmitted } from '../utils/checkoutFlowUtil';
import '../css/UserInfoForm.css';
import '../css/CheckoutTabPending.css';

/**
 * Card copy per checkout-tab state.
 * @param {string} submitLabel - the checkout page's final button, quoted in the text.
 * @param {string} closedNote - what happens to the hold while the tab is closed.
 * @returns {Object<string, {text: string, primary: string}>}
 */
const buildCopy = (submitLabel, closedNote) => ({
  [CHECKOUT_TAB_STATES.BLOCKED]: {
    text: 'Your browser didn’t open the checkout page automatically. Tap the button below to open it in a new tab.',
    primary: 'Open checkout',
  },
  [CHECKOUT_TAB_STATES.CLOSED]: {
    text: `The checkout tab was closed. ${closedNote}`,
    primary: 'Reopen checkout',
  },
  default: {
    text: `Your checkout is open in a new tab. Pay and upload your receipt there, then tap “${submitLabel}” — you’ll be brought back here automatically.`,
    primary: 'Go to checkout tab',
  },
});

/**
 * Card shown in the opening tab while a checkout step is open in its own tab.
 * Lets the guest jump back to (or reopen) that tab, or back out. Used by the
 * court-booking flow and the Open Play join flow; the text props default to
 * the court-booking wording.
 *
 * @param {Object} props
 * @param {Object} props.bookingData - the booking (or Open Play join) being paid for.
 * @param {string} props.tabState - CHECKOUT_TAB_STATES value.
 * @param {() => void} props.onOpenCheckout - focus / reopen the checkout tab (runs in the click, so window.open is allowed).
 * @param {() => void} props.onCancel - the secondary button (cancel / go back).
 * @param {() => void} props.onExpire - payment deadline passed; called once.
 * @param {() => void} [props.onClose] - the × button; defaults to onCancel.
 * @param {string} [props.submitLabel] - name of the checkout page's final button.
 * @param {string} [props.cancelLabel] - label of the secondary button.
 * @param {string} [props.referenceLabel] - heading above the reference number.
 * @param {string} [props.expiryNote] - end of the timer sentence.
 * @param {string} [props.closedNote] - text shown after "The checkout tab was closed."
 * @returns {JSX.Element}
 */
function CheckoutTabPending({
  bookingData,
  tabState,
  onOpenCheckout,
  onCancel,
  onExpire,
  onClose = null,
  submitLabel = 'Submit Booking',
  cancelLabel = 'Cancel booking',
  referenceLabel = 'Booking reference',
  expiryNote = 'or your reservation will be released.',
  closedNote = 'Your reservation is still held until the timer runs out — reopen it to finish your payment.',
}) {
  const submitted = isBookingSubmitted(bookingData);
  const [msLeft, setMsLeft] = useState(() => msUntil(bookingData?.paymentDeadline));
  const expiredRef = useRef(false);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  // Same clock as the checkout tab. Stops once the receipt is in, since the
  // deadline no longer applies.
  useEffect(() => {
    if (!bookingData?.paymentDeadline || submitted) return undefined;
    const tick = () => {
      const remaining = msUntil(bookingData.paymentDeadline);
      setMsLeft(remaining);
      if (remaining !== null && remaining <= 0 && !expiredRef.current) {
        expiredRef.current = true;
        onExpireRef.current?.();
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [bookingData?.paymentDeadline, submitted]);

  const copyByState = buildCopy(submitLabel, closedNote);
  const copy = copyByState[tabState] || copyByState.default;
  const text = submitted
    ? `We’ve received your payment receipt. Tap “${submitLabel}” in the checkout tab to finish.`
    : copy.text;
  const handleClose = onClose || onCancel;
  const closeLabel = submitted || onClose ? 'Close' : cancelLabel;

  return (
    <div className="user-info-form checkout-tab-pending">
      <div className="uif-card">
        <div className="uif-head">
          <h2 className="uif-title">Complete your payment</h2>
          <button
            type="button"
            className="uif-close"
            onClick={handleClose}
            aria-label={closeLabel}
            title={closeLabel}
          >&times;</button>
        </div>

        <p className="ctp-text">{text}</p>

        {bookingData?.confirmationId && (
          <div className="ctp-ref">
            <div className="ctp-ref-label">{referenceLabel}</div>
            <div className="ctp-ref-value">{bookingData.confirmationId}</div>
          </div>
        )}

        {msLeft !== null && !submitted && (
          <div className="ctp-timer">
            Complete payment within <strong>{formatCountdown(msLeft)}</strong> {expiryNote}
          </div>
        )}

        <div className="ctp-actions">
          <button type="button" className="ctp-btn ctp-btn-primary" onClick={onOpenCheckout}>
            {copy.primary}
          </button>
          {!submitted && (
            <button type="button" className="ctp-btn ctp-btn-secondary" onClick={onCancel}>
              {cancelLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default CheckoutTabPending;
